<template>
  <component
    :is="tag"
    class="media-card"
    :class="classes"
    :style="{
      '--backgroundImageSmall': `url(${images?.smallMedia})`,
      '--backgroundImageMedium': `url(${images?.mediumMedia})`,
      '--backgroundImageLarge': `url(${images?.largeMedia})`,
    }"
    :href="link"
  >
    <div class="media-card__image">
      <IntersectionObserver
        v-if="videoUrl"
        v-slot="{ intersected }"
        class="video-intersection-observer"
      >
        <Video
          :intersected="intersected"
          :video="{
            sources: [
              {
                url: videoUrl,
              },
            ],
          }"
        >
        </Video>
      </IntersectionObserver>
      <h2 v-if="title">{{ title }}</h2>
      <template v-if="showTextWithinPicture">
        <p v-if="text" class="text">{{ text }}</p>
        <div class="cta-group">
          <slot name="cta"></slot>
        </div>
      </template>
    </div>
    <div v-if="showText" class="media-card__text">
      <div v-if="!showTextWithinPicture" class="cta-group">
        <p v-if="text" class="text">{{ text }}</p>
        <slot name="cta"></slot>
      </div>
    </div>
  </component>
</template>

<script>
import IntersectionObserver from "@/components/IntersectionObserver.vue";
import Video from "@/components/Video.vue";

export default {
  name: "MediaCard",
  components: {
    IntersectionObserver,
    Video,
  },
  inject: ["mq"],
  props: {
    title: {
      type: String,
      default: null,
    },
    text: {
      type: String,
      default: null,
    },
    alignTitle: {
      type: String,
      default: "bottom",
      validator(value) {
        return ["top", "middle", "bottom"].includes(value);
      },
    },
    videoUrl: {
      type: String,
      default: null,
    },
    images: {
      type: Object,
      required: true,
    },
    link: {
      type: String,
      default: null,
    },
  },
  computed: {
    classes() {
      return {
        "media-card--just-cta": this.isJustCTA,
        "media-card--just-title": this.isJustTitle,
        "media-card--align-title-top": this.alignTitle == "top",
        "media-card--align-title-middle": this.alignTitle == "middle",
      };
    },
    isJustCTA() {
      const hasCTA = this.ctaSlotExists;
      const hasTitle = !!this.title;
      const hasText = !!this.text;

      return hasCTA && !hasTitle && !hasText;
    },
    isJustTitle() {
      const hasCTA = this.ctaSlotExists;
      const hasTitle = !!this.title;
      const hasText = !!this.text;

      return !hasCTA && hasTitle && !hasText;
    },
    ctaSlotExists() {
      return !!this.$slots["cta"];
    },
    showTextWithinPicture() {
      if (this.mq.mdPlus) {
        return true;
      }

      return this.isJustCTA;
    },
    showText() {
      if (this.mq.mdPlus) {
        return false;
      }

      return !!this.text && !!this.ctaSlotExists;
    },
    tag() {
      if (this.link) {
        return "a";
      }

      return "div";
    },
  },
};
</script>

<style lang="scss" scoped>
.media-card {
  text-align: center;
  text-decoration: none;

  &__image {
    color: $color-white;
    aspect-ratio: 0.75;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 45px;
    position: relative;

    background-image: var(--backgroundImageSmall);
    background-size: cover;
    background-position: center center;

    @include respond-above(sm) {
      background-image: var(--backgroundImageMedium);
    }

    @include respond-above(md) {
      aspect-ratio: auto;
      height: 90vh;
      justify-content: end;
      background-image: var(--backgroundImageLarge);
    }

    &::after {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 50%,
        rgba(0, 0, 0, 0.5) 90%
      );
    }
  }

  h2 {
    font-family: $heading-font-family;
    font-size: 36px;
    text-transform: uppercase;
    font-weight: 500;
    color: $color-white;
    z-index: 1;
    text-decoration: none;
  }

  .text {
    font-family: $heading-font-family;
    font-size: 16px;
    margin: 0 auto;
    z-index: 1;

    @include respond-above(md) {
      width: 60%;
      max-width: 440px;
      margin-top: 10px;
      margin-bottom: 30px;
    }
  }
  .cta-group {
    z-index: 1;
  }

  ::v-deep(.button) {
    color: $color-white;
    font-size: 20px;
    font-weight: 500;
    font-family: $heading-font-family;
    text-decoration: none;
    display: inline-block;
  }
  ::v-deep(.button + .button) {
    margin-left: 30px;
  }

  &__text {
    padding: 36px;

    ::v-deep(.button) {
      margin-top: 10px;
      color: $color-dark;
      font-size: 16px;
    }
  }

  .video-intersection-observer {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;

    ::v-deep(.video__element) {
      object-fit: cover;
    }
  }
}

.media-card--just-cta .media-card__image {
  justify-content: end;
}

.media-card--just-title .media-card__image {
  justify-content: center;
}

.media-card--align-title-top .media-card__image h2 {
  @include respond-above(md) {
    flex: 1 0 auto;
  }
}

.media-card--align-title-middle .media-card__image h2 {
  @include respond-above(md) {
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
}
</style>
